/**
 * `/interests/new` — a lead that did not come through the website.
 *
 * Somebody rang, or was met at an event, or wrote to a personal inbox. The
 * owner asked for those to live beside the form submissions "so that all the
 * sales tickets are at one place", so this writes the same kind of row with
 * `source: 'admin'` and the staff member's address recorded as its author.
 *
 * Email **or** phone, not both: a person who rang may have left only a number.
 * The rule is checked here against the shared contract before anything is
 * sent, by the API against the same contract, and by 0040's
 * `interests_reachable` CHECK — three places, one sentence.
 *
 * It can be created already in progress, because a lead typed in after a call
 * usually is. On success it goes straight to the interest, where the first note
 * belongs.
 */
import { ArrowLeft } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { createInterestRequestSchema, type InterestStatus } from '@wend/shared';
import { Button, Card, CardContent, FormField, Input, useToast } from '@wend/ui';

import { AdminShell } from '../components/AdminShell';
import { FormAlert } from '../components/FormAlert';
import { Textarea } from '../components/Textarea';
import { describeApiError, toApiError, type ApiError } from '../lib/api-error';
import { NO_FIELD_ERRORS, toFieldErrorMap, zodFieldErrors, type FieldErrorMap } from '../lib/form';
import { INTEREST_STATUS_LABEL } from '../lib/interest-status';
import { createInterest } from '../lib/interests-api';

interface InterestFormState {
  readonly name: string;
  readonly organisation: string;
  readonly email: string;
  readonly phone: string;
  readonly message: string;
  readonly status: InterestStatus;
}

const EMPTY_FORM: InterestFormState = {
  name: '',
  organisation: '',
  email: '',
  phone: '',
  message: '',
  status: 'new',
};

/** Only these two make sense for a lead being written down for the first time. */
const STARTING_STATUSES: readonly InterestStatus[] = ['new', 'in_progress'];

export function NewInterestPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState<InterestFormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrorMap>(NO_FIELD_ERRORS);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [localMessage, setLocalMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (patch: Partial<InterestFormState>): void => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    setLocalMessage(null);

    const parsed = createInterestRequestSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      setLocalMessage('Some details need attention. Check the highlighted fields.');
      return;
    }

    setErrors(NO_FIELD_ERRORS);
    setSubmitting(true);

    try {
      const created = await createInterest(form);
      toast({ tone: 'success', title: 'Interest added', description: `${created.name}, ${created.organisation}.` });
      navigate(`/interests/${created.id}`);
    } catch (error: unknown) {
      const apiError = toApiError(error);
      setFailure(apiError);
      setErrors(toFieldErrorMap(apiError.fieldErrors));
      setSubmitting(false);
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit();
  };

  return (
    <AdminShell title="New interest">
      <form onSubmit={onSubmit} noValidate className="flex max-w-3xl flex-col gap-5">
        <Link
          to="/interests"
          className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted transition-colors hover:text-text"
        >
          <ArrowLeft aria-hidden="true" className="size-3.5" />
          Interests
        </Link>

        {failure !== null && (
          <FormAlert requestId={failure.requestId}>{describeApiError(failure)}</FormAlert>
        )}
        {failure === null && localMessage !== null && <FormAlert>{localMessage}</FormAlert>}

        <Card>
          <CardContent className="flex flex-col gap-5 pt-6">
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField label="Name" required error={errors['name']}>
                <Input
                  value={form.name}
                  autoComplete="off"
                  onChange={(event) => {
                    update({ name: event.target.value });
                  }}
                />
              </FormField>

              <FormField label="Company" required error={errors['organisation']}>
                <Input
                  value={form.organisation}
                  autoComplete="off"
                  onChange={(event) => {
                    update({ organisation: event.target.value });
                  }}
                />
              </FormField>

              <FormField
                label="Email"
                description="Email or phone — at least one, so somebody can follow up."
                error={errors['email']}
              >
                <Input
                  type="email"
                  value={form.email}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => {
                    update({ email: event.target.value });
                  }}
                />
              </FormField>

              <FormField label="Phone" error={errors['phone']}>
                <Input
                  type="tel"
                  value={form.phone}
                  autoComplete="off"
                  onChange={(event) => {
                    update({ phone: event.target.value });
                  }}
                />
              </FormField>
            </div>

            <FormField
              label="What they asked for"
              description="Their words, as near as you have them. Your own notes go on the timeline after this is saved."
              error={errors['message']}
            >
              <Textarea
                rows={5}
                value={form.message}
                onChange={(event) => {
                  update({ message: event.target.value });
                }}
              />
            </FormField>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-[0.85rem] font-medium">Status</legend>
              <div className="flex flex-wrap gap-4">
                {STARTING_STATUSES.map((value) => (
                  <label key={value} className="inline-flex items-center gap-2 text-[0.9rem]">
                    <input
                      type="radio"
                      name="status"
                      value={value}
                      checked={form.status === value}
                      onChange={() => {
                        update({ status: value });
                      }}
                      className="accent-brand-a"
                    />
                    {INTEREST_STATUS_LABEL[value]}
                  </label>
                ))}
              </div>
            </fieldset>
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button type="submit" loading={submitting}>
            Add interest
          </Button>
          <Link to="/interests" className="text-[0.88rem] text-muted underline-offset-4 hover:underline">
            Cancel
          </Link>
        </div>
      </form>
    </AdminShell>
  );
}
